/**
 * Setup-wizard step components — Phase 24 / Task 24.8 (DEC-099).
 *
 * Each step is self-contained and usable BOTH inside the full-screen wizard and
 * standalone in Settings → Getting started. Steps 2/3 reuse the
 * Task 24.3 OperatorsTab / DisposalWellsTab; steps 4/5/6 reuse the Phase 9 fleet
 * hooks (minimal create forms); step 7 lists job types (per-tenant rate/destination
 * config DEFERRED — no schema yet, DEC-099); the driver-invite flow is DEFERRED
 * (no Cognito/SMS provisioning exists yet).
 */
import * as React from 'react';
import { toast } from 'sonner';
import { Button } from 'src/components/ui/button';
import { fieldStyle, labelStyle, FormRow } from '../sites-operators/form-bits';
import { AddButton, Pill, rowStyle, subText } from '../sites-operators/ui-bits';
import { OperatorsTab } from '../sites-operators/OperatorsTab';
import { DisposalWellsTab } from '../sites-operators/DisposalWellsTab';
import {
  useFleetYards,
  useCreateYard,
  useFleetTrucks,
  useCreateTruck,
  useFleetTrailers,
  useCreateTrailer,
  useFleetDrivers,
} from '../fleet/hooks';
import { JobTypesEditor } from './JobTypesEditor';
import { useHaulerOperators, useDisposalFacilities } from '../sites-operators/hooks';
import { useCompanyInfo, useUpdateCompanyInfo } from './hooks';
import { ABBREVIATION_RE, ABBREVIATION_RULE_TEXT } from '@lodex-pro/shared/abbreviation';

export interface StepProps {
  readonly canManage: boolean;
  readonly isTenantAdmin: boolean;
}

const sectionTitle: React.CSSProperties = {
  fontSize: '13px',
  fontWeight: 700,
  color: 'var(--color-text-tertiary)',
  margin: '4px 0 10px',
};

// ── Step 1 — Company Info ────────────────────────────────────────────────────

export function CompanyInfoStep({ canManage }: StepProps): React.JSX.Element {
  const { data } = useCompanyInfo();
  const update = useUpdateCompanyInfo();
  const [name, setName] = React.useState('');
  const [dot, setDot] = React.useState('');
  const [phone, setPhone] = React.useState('');
  const [email, setEmail] = React.useState('');
  const [address, setAddress] = React.useState('');
  const [abbrev, setAbbrev] = React.useState('');
  const [error, setError] = React.useState<string | null>(null);

  React.useEffect(() => {
    if (data === undefined) return;
    setName(data.name);
    setDot(data.dotNumber ?? '');
    setPhone(data.primaryPhone ?? '');
    setEmail(data.primaryEmail ?? '');
    setAddress(data.businessAddress ?? '');
    setAbbrev(data.haulerAbbreviation ?? '');
  }, [data]);

  const abbrevLocked = data?.haulerAbbreviationLocked === true;

  const save = (): void => {
    if (name.trim() === '') {
      setError('Company name is required.');
      return;
    }
    if (!abbrevLocked && abbrev.trim() !== '' && !ABBREVIATION_RE.test(abbrev.trim())) {
      setError(ABBREVIATION_RULE_TEXT);
      return;
    }
    setError(null);
    update.mutate(
      {
        name: name.trim(),
        dotNumber: dot.trim() === '' ? null : dot.trim(),
        primaryPhone: phone.trim() === '' ? null : phone.trim(),
        primaryEmail: email.trim() === '' ? null : email.trim(),
        businessAddress: address.trim() === '' ? null : address.trim(),
        ...(!abbrevLocked && abbrev.trim() !== '' ? { haulerAbbreviation: abbrev.trim() } : {}),
      },
      {
        onSuccess: () => toast.success('Company info saved'),
        onError: () => setError('Could not save. Check your inputs and try again.'),
      },
    );
  };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '12px', maxWidth: '480px' }}>
      <FormRow label="Company name" htmlFor="ci-name" required>
        <input
          id="ci-name"
          value={name}
          onChange={(e): void => setName(e.target.value)}
          style={fieldStyle}
        />
      </FormRow>
      <FormRow label="DOT number" htmlFor="ci-dot">
        <input
          id="ci-dot"
          value={dot}
          onChange={(e): void => setDot(e.target.value)}
          style={fieldStyle}
        />
      </FormRow>
      <FormRow label="Primary phone" htmlFor="ci-phone">
        <input
          id="ci-phone"
          value={phone}
          onChange={(e): void => setPhone(e.target.value)}
          style={fieldStyle}
        />
      </FormRow>
      <FormRow label="Primary email" htmlFor="ci-email">
        <input
          id="ci-email"
          type="email"
          value={email}
          onChange={(e): void => setEmail(e.target.value)}
          style={fieldStyle}
        />
      </FormRow>
      <FormRow label="Business address" htmlFor="ci-addr">
        <input
          id="ci-addr"
          value={address}
          onChange={(e): void => setAddress(e.target.value)}
          style={fieldStyle}
        />
      </FormRow>
      <FormRow label="Hauler abbreviation (letters only, 2–4)" htmlFor="ci-abbrev">
        <input
          id="ci-abbrev"
          value={abbrev}
          maxLength={4}
          readOnly={abbrevLocked}
          onChange={(e): void => setAbbrev(e.target.value.toUpperCase())}
          placeholder="HAU"
          style={{
            ...fieldStyle,
            textTransform: 'uppercase',
            maxWidth: '140px',
            opacity: abbrevLocked ? 0.7 : 1,
          }}
        />
      </FormRow>
      <p style={{ ...labelStyle, textTransform: 'none' }}>
        Your ticket IDs will look like:{' '}
        <span style={{ fontFamily: 'var(--font-mono, monospace)' }}>
          {(abbrev.trim() || 'HAU').toUpperCase()}-OPR000000001
        </span>
        {abbrevLocked ? ' (abbreviation set by Paelode — read-only)' : ''}
      </p>
      {error !== null && (
        <p role="alert" style={{ ...labelStyle, color: 'var(--color-danger)' }}>
          {error}
        </p>
      )}
      {canManage && (
        <div>
          <Button
            type="button"
            disabled={update.isPending}
            onClick={save}
            style={{
              backgroundColor: 'var(--color-accent-blue)',
              color: 'var(--color-on-accent)',
              borderColor: 'var(--color-accent-blue)',
            }}
          >
            {update.isPending ? 'Saving…' : 'Save'}
          </Button>
        </div>
      )}
    </div>
  );
}

// ── Step 2 / 3 — reuse the Task 24.3 tabs ────────────────────────────────────

export function OperatorsStep({ canManage, isTenantAdmin }: StepProps): React.JSX.Element {
  return <OperatorsTab canManage={canManage} isTenantAdmin={isTenantAdmin} />;
}

export function DisposalStep({ canManage }: StepProps): React.JSX.Element {
  return <DisposalWellsTab canManage={canManage} />;
}

// ── Step 4 — Yards ───────────────────────────────────────────────────────────

export function YardsStep({ canManage }: StepProps): React.JSX.Element {
  const { data } = useFleetYards();
  const [adding, setAdding] = React.useState(false);
  const [name, setName] = React.useState('');
  const [address, setAddress] = React.useState('');
  const [lat, setLat] = React.useState('');
  const [lng, setLng] = React.useState('');
  const create = useCreateYard(() => {
    toast.success('Yard added');
    setAdding(false);
    setName('');
    setAddress('');
    setLat('');
    setLng('');
  });
  const yards = data?.yards.filter((y) => y.isActive) ?? [];

  const num = (s: string): number | null => (s.trim() === '' ? null : Number(s));

  return (
    <div>
      <div
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          marginBottom: '8px',
        }}
      >
        <div style={sectionTitle}>Yards ({yards.length})</div>
        {canManage && !adding && (
          <AddButton label="Add Yard" onClick={(): void => setAdding(true)} />
        )}
      </div>
      {yards.length === 0 && <p style={subText}>No yards yet.</p>}
      {yards.map((y) => (
        <div key={y.id} style={rowStyle}>
          <div style={{ flex: 1 }}>
            <span style={{ fontWeight: 600 }}>{y.name}</span>
            <div style={subText}>
              {y.address ?? 'No address'}
              {y.gpsLat != null && y.gpsLng != null ? ` · ${y.gpsLat}, ${y.gpsLng}` : ''}
            </div>
          </div>
        </div>
      ))}
      {adding && (
        <div
          style={{
            display: 'flex',
            flexDirection: 'column',
            gap: '10px',
            marginTop: '12px',
            maxWidth: '420px',
          }}
        >
          <FormRow label="Name" htmlFor="yard-name" required>
            <input
              id="yard-name"
              value={name}
              onChange={(e): void => setName(e.target.value)}
              style={fieldStyle}
            />
          </FormRow>
          <FormRow label="Address" htmlFor="yard-addr">
            <input
              id="yard-addr"
              value={address}
              onChange={(e): void => setAddress(e.target.value)}
              style={fieldStyle}
            />
          </FormRow>
          <div style={{ display: 'flex', gap: '10px' }}>
            <FormRow label="GPS lat" htmlFor="yard-lat">
              <input
                id="yard-lat"
                inputMode="decimal"
                value={lat}
                onChange={(e): void => setLat(e.target.value)}
                style={fieldStyle}
              />
            </FormRow>
            <FormRow label="GPS lng" htmlFor="yard-lng">
              <input
                id="yard-lng"
                inputMode="decimal"
                value={lng}
                onChange={(e): void => setLng(e.target.value)}
                style={fieldStyle}
              />
            </FormRow>
          </div>
          <div style={{ display: 'flex', gap: '8px' }}>
            <Button type="button" variant="outline" onClick={(): void => setAdding(false)}>
              Cancel
            </Button>
            <Button
              type="button"
              disabled={create.isPending || name.trim() === ''}
              onClick={(): void =>
                create.mutate(
                  {
                    name: name.trim(),
                    address: address.trim() === '' ? null : address.trim(),
                    gpsLat: num(lat),
                    gpsLng: num(lng),
                  },
                  { onError: () => toast.error('Could not add yard') },
                )
              }
              style={{
                backgroundColor: 'var(--color-accent-blue)',
                color: 'var(--color-on-accent)',
                borderColor: 'var(--color-accent-blue)',
              }}
            >
              {create.isPending ? 'Adding…' : 'Add yard'}
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}

// ── Step 5 — Trucks & Trailers ───────────────────────────────────────────────

export function FleetStep({ canManage }: StepProps): React.JSX.Element {
  const { data: truckData } = useFleetTrucks();
  const { data: trailerData } = useFleetTrailers();
  const trucks = truckData?.trucks.filter((t) => t.isActive) ?? [];
  const trailers = trailerData?.trailers.filter((t) => t.isActive) ?? [];

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
      <UnitAddList
        title="Trucks"
        canManage={canManage}
        items={trucks.map((t) => ({ id: t.id, label: t.unitNumber, sub: t.truckType ?? '' }))}
        useCreate={useCreateTruck}
      />
      <UnitAddList
        title="Trailers"
        canManage={canManage}
        items={trailers.map((t) => ({ id: t.id, label: t.unitNumber, sub: t.trailerType ?? '' }))}
        useCreate={useCreateTrailer}
      />
    </div>
  );
}

/** Shared unit list + minimal "unit number + type" add form for trucks/trailers. */
function UnitAddList({
  title,
  canManage,
  items,
  useCreate,
}: {
  title: string;
  canManage: boolean;
  items: ReadonlyArray<{ id: string; label: string; sub: string }>;
  useCreate: (onDone: () => void) => {
    mutate: (body: { unitNumber: string }, opts?: { onError?: () => void }) => void;
    isPending: boolean;
  };
}): React.JSX.Element {
  const [adding, setAdding] = React.useState(false);
  const [unit, setUnit] = React.useState('');
  const create = useCreate(() => {
    toast.success(`${title.slice(0, -1)} added`);
    setAdding(false);
    setUnit('');
  });
  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <div style={sectionTitle}>
          {title} ({items.length})
        </div>
        {canManage && !adding && (
          <AddButton label={`Add ${title.slice(0, -1)}`} onClick={(): void => setAdding(true)} />
        )}
      </div>
      {items.length === 0 && <p style={subText}>None yet.</p>}
      {items.map((it) => (
        <div key={it.id} style={rowStyle}>
          <span style={{ fontWeight: 600 }}>{it.label}</span>
          {it.sub !== '' && <span style={subText}>· {it.sub}</span>}
        </div>
      ))}
      {adding && (
        <div style={{ display: 'flex', gap: '8px', alignItems: 'flex-end', marginTop: '8px' }}>
          <FormRow label="Unit number" htmlFor={`unit-${title}`} required>
            <input
              id={`unit-${title}`}
              value={unit}
              onChange={(e): void => setUnit(e.target.value)}
              style={{ ...fieldStyle, maxWidth: '160px' }}
            />
          </FormRow>
          <Button type="button" variant="outline" onClick={(): void => setAdding(false)}>
            Cancel
          </Button>
          <Button
            type="button"
            disabled={create.isPending || unit.trim() === ''}
            onClick={(): void =>
              create.mutate(
                { unitNumber: unit.trim() },
                { onError: () => toast.error('Could not add') },
              )
            }
            style={{
              backgroundColor: 'var(--color-accent-blue)',
              color: 'var(--color-on-accent)',
              borderColor: 'var(--color-accent-blue)',
            }}
          >
            Add
          </Button>
        </div>
      )}
    </div>
  );
}

// ── Step 6 — Drivers (edit-only; invite DEFERRED) ────────────────────────────

export function DriversStep({ canManage }: StepProps): React.JSX.Element {
  const { data } = useFleetDrivers();
  const drivers = data?.drivers ?? [];
  return (
    <div>
      <div
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          marginBottom: '8px',
        }}
      >
        <div style={sectionTitle}>Drivers ({drivers.length})</div>
        {canManage && (
          <Button type="button" variant="outline" disabled title="Driver invites are coming soon">
            + Invite Driver (coming soon)
          </Button>
        )}
      </div>
      {drivers.length === 0 && <p style={subText}>No drivers yet.</p>}
      {drivers.map((d) => (
        <div key={d.driverId} style={rowStyle}>
          <div style={{ flex: 1 }}>
            <span style={{ fontWeight: 600 }}>{d.driverName}</span>
            <div style={subText}>
              {d.shift} shift
              {d.yardName != null ? ` · ${d.yardName}` : ''}
              {d.cdlNumber != null ? ` · CDL ${d.cdlNumber}` : ''}
            </div>
          </div>
        </div>
      ))}
      <p style={{ ...subText, marginTop: '8px' }}>
        Driver invitations (email/SMS) are a tracked follow-up. Existing drivers are managed in the
        Fleet console.
      </p>
    </div>
  );
}

// ── Step 7 — Job Types (tenant-owned CRUD, DEC-140) ───────────────────────────

export function JobTypesStep(): React.JSX.Element {
  return (
    <div>
      <div style={sectionTitle}>Job Types</div>
      <JobTypesEditor />
    </div>
  );
}

// ── Step 8 — Review & Launch ─────────────────────────────────────────────────

export function ReviewStep({
  status,
  onLaunch,
  launching,
}: {
  status: readonly boolean[];
  onLaunch: () => void;
  launching: boolean;
}): React.JSX.Element {
  const { data: company } = useCompanyInfo();
  const { data: ops } = useHaulerOperators();
  const { data: disp } = useDisposalFacilities();
  const { data: yards } = useFleetYards();
  const { data: trucks } = useFleetTrucks();
  const { data: trailers } = useFleetTrailers();
  const { data: drivers } = useFleetDrivers();

  const line = (label: string, value: string): React.JSX.Element => (
    <div style={{ ...rowStyle }}>
      <span style={{ flex: 1, fontWeight: 600 }}>{label}</span>
      <span style={subText}>{value}</span>
    </div>
  );
  const labels = [
    'Company',
    'Operators',
    'Disposal wells',
    'Yards',
    'Trucks & trailers',
    'Drivers',
    'Job types',
  ];

  return (
    <div style={{ maxWidth: '520px' }}>
      <div style={sectionTitle}>Summary</div>
      {line('Company', company?.name ?? '—')}
      {line('Operators / customers', String(ops?.haulerOperators.length ?? 0))}
      {line('Disposal wells', String(disp?.haulerDisposalFacilities.length ?? 0))}
      {line('Yards', String(yards?.yards.filter((y) => y.isActive).length ?? 0))}
      {line(
        'Trucks / trailers',
        `${trucks?.trucks.filter((t) => t.isActive).length ?? 0} / ${trailers?.trailers.filter((t) => t.isActive).length ?? 0}`,
      )}
      {line('Drivers', String(drivers?.drivers.length ?? 0))}

      <div style={{ ...sectionTitle, marginTop: '16px' }}>Section status</div>
      {labels.map((l, i) => (
        <div key={l} style={rowStyle}>
          <span style={{ flex: 1 }}>{l}</span>
          {status[i] ? (
            <Pill label="Complete" tone="positive" />
          ) : (
            <Pill label="Incomplete" tone="muted" />
          )}
        </div>
      ))}

      <div style={{ marginTop: '20px' }}>
        <Button
          type="button"
          disabled={launching}
          onClick={onLaunch}
          style={{
            backgroundColor: 'var(--color-success)',
            color: 'var(--color-on-accent)',
            borderColor: 'var(--color-success)',
          }}
        >
          {launching ? 'Launching…' : 'Launch lodeX Pro'}
        </Button>
      </div>
    </div>
  );
}
